import ontick_colors from "./ontick_colors";

const { green, orange, blue, steel } = ontick_colors;

const default_watermark_options = {
  show: true,
  single_color: false,
  color: steel[400],
  value: null,
  draw_value: null,
  dashed: true,
  dash_array: "4",
};

export class Spark {
  // Data
  container_id;
  width;
  height;
  padding;
  watermark;
  dataset;
  color;
  positive_color;
  negative_color;
  stroke_width;
  stroke_linecap;
  positive_mask_id;
  negative_mask_id;
  shadow;

  // Elements
  container;
  svg;
  defs;
  base_line;
  path;
  positive_path;
  positive_mask;
  positive_mask_rect;
  negative_path;
  negative_mask;
  negative_mask_rect;
  horizontal_interval;
  max_value;
  min_value;
  values_to_height;
  draw_width;
  draw_height;

  constructor({
    container_id,
    width,
    height,
    padding = 8,
    watermark = { ...default_watermark_options },
    dataset = [100, 200, 150, 50, 100, 150],
    color = blue[600],
    positive_color = green[600],
    negative_color = orange[600],
    stroke_width = 2,
    stroke_linecap = "round",
    shadow = true,
  }) {
    this.container_id = container_id;
    this.width = width;
    this.height = height;
    this.padding = padding;
    this.watermark = watermark;
    this.dataset = dataset;
    this.color = color;
    this.positive_color = positive_color;
    this.negative_color = negative_color;
    this.stroke_width = stroke_width;
    this.stroke_linecap = stroke_linecap;
    this.shadow = shadow;
  }

  GetOptions() {
    return {
      container_id: this.container_id,
      width: this.width,
      height: this.height,
      padding: this.padding,
      watermark: this.watermark,
      dataset: this.dataset,
      color: this.color,
      positive_color: this.positive_color,
      negative_color: this.negative_color,
    };
  }

  CreateSvgElements(arr) {
    arr.forEach((item) => {
      const prop_name = item[0];
      const html_element = item[1];
      const element = document.createElementNS("http://www.w3.org/2000/svg", html_element);
      this[prop_name] = element;
    });
  }

  SetContainer() {
    this.container = document.getElementById(this.container_id);
    this.SetAttrs(this.container, [
      ["width", this.width],
      ["height", this.height],
    ]);
    this.ResetContainer();
  }

  Setup() {
    // Create elements
    this.SetContainer();
    if (this.watermark.show && this.watermark.single_color) {
      this.path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    } else {
      this.CreateSvgElements([
        ["svg", "svg"],
        ["defs", "defs"],
        ["base_line", "line"],
        ["positive_path", "path"],
        ["negative_path", "path"],
        ["positive_mask", "mask"],
        ["positive_mask_rect", "rect"],
        ["negative_mask", "mask"],
        ["negative_mask_rect", "rect"],
      ]);
    }
  }

  ValueToHeight(value) {
    const min = this.min_value;
    const max = this.max_value;
    const draw_height = this.draw_height;
    const padding = this.padding;

    return ((value - min) / (max - min)) * (draw_height - padding * 2);
  }

  DrawHeight(value) {
    return this.draw_height - (value + this.padding);
  }

  SetBoundaries() {
    // Set boundaries
    this.draw_width = this.width - this.padding * 4;
    this.draw_height = this.height;

    this.horizontal_interval = this.draw_width / this.dataset.length;
    this.max_value = Math.max(...this.dataset);
    this.min_value = Math.min(...this.dataset);
    this.values_to_height = this.dataset
      .map((value) => {
        if (!value) return 0;
        return this.ValueToHeight(value);
      })
      .reverse();
    this.watermark.draw_value = this.ValueToHeight(this.watermark.value);
  }

  ResetContainer() {
    // Resets container
    this.container.innerHTML = "";
  }

  SetSvgSize() {
    // Configures canvas
    this.svg.setAttribute("width", this.draw_width);
    this.svg.setAttribute("height", this.draw_height);
  }

  SetAttrs(element, attributes) {
    attributes.forEach((attr) => {
      const value = Number(attr[1]);
      const attr1 = Number.isNaN(value) ? attr[1] : Math.abs(value);

      element.setAttribute(attr[0], attr1);
    });
  }

  Draw() {
    // Draws
    let steps = [];
    for (let i = 1; i < this.values_to_height.length; i++) {
      const previous_x = this.horizontal_interval * (i - 1) + this.padding / 2;
      const previous_y = this.DrawHeight(this.values_to_height[i - 1]);
      const next_x = this.horizontal_interval * i + this.padding / 2;
      const next_y = this.DrawHeight(this.values_to_height[i]);

      steps.push(`M${previous_x} ${previous_y}`);
      steps.push(`L${next_x} ${next_y}`);
    }
    const path_attr = steps.join(" ");

    if (this.watermark.show) {
      if (this.watermark.single_color) {
        this.SetAttrs(this.path, [
          ["d", path_attr],
          [
            "style",
            `fill: none; stroke: ${this.color}; stroke-width: ${this.stroke_width}; stroke-linecap: ${this.stroke_linecap}`,
          ],
        ]);
      } else {
        this.SetAttrs(this.positive_path, [
          ["d", path_attr],
          [
            "style",
            `fill: none; stroke: ${this.positive_color}; stroke-width: ${this.stroke_width}; stroke-linecap: ${this.stroke_linecap}`,
          ],
        ]);
        this.SetAttrs(this.negative_path, [
          ["d", path_attr],
          [
            "style",
            `fill: none; stroke: ${this.negative_color}; stroke-width: ${this.stroke_width}; stroke-linecap: ${this.stroke_linecap}`,
          ],
        ]);
      }
      const base_line_attrs = [
        ["x1", 0],
        ["x2", this.draw_width],
        ["y1", this.DrawHeight(this.watermark.draw_value)],
        ["y2", this.DrawHeight(this.watermark.draw_value)],
        [
          "style",
          `fill: none; stroke: ${this.watermark.color}; stroke-width: ${this.stroke_width}; stroke-dasharray: ${this.watermark.dash_array}`,
        ],
      ];
      this.positive_mask_id = `positive_mask-${this.container_id}`;
      const positive_mask_attrs = [
        ["id", this.positive_mask_id],
        ["x", "0"],
        ["y", "0"],
        ["width", "100%"],
        ["height", "100%"],
      ];
      this.negative_mask_id = `negative_mask-${this.container_id}`;
      this.negative_mask_attrs = [
        ["id", this.negative_mask_id],
        ["x", "0"],
        ["y", "0"],
        ["width", "100%"],
        ["height", "100%"],
      ];
      const positive_mask_rect_attrs = [
        ["x", "0"],
        ["y", "0"],
        ["width", "100%"],
        ["height", `${this.draw_height - (this.watermark.draw_value + this.padding)}`],
        ["style", "fill: #fff"],
      ];
      const negative_mask_rect_attrs = [
        ["x", "0"],
        ["y", `${this.DrawHeight(this.watermark.draw_value)}`],
        ["width", "100%"],
        ["height", "100%"],
        ["style", "fill: #fff"],
      ];

      this.SetAttrs(this.base_line, base_line_attrs);
      this.SetAttrs(this.positive_mask, positive_mask_attrs);
      this.SetAttrs(this.negative_mask, this.negative_mask_attrs);
      this.SetAttrs(this.positive_mask_rect, positive_mask_rect_attrs);
      this.SetAttrs(this.negative_mask_rect, negative_mask_rect_attrs);

      this.positive_mask.appendChild(this.positive_mask_rect);
      this.negative_mask.appendChild(this.negative_mask_rect);
      this.defs.appendChild(this.positive_mask);
      this.defs.appendChild(this.negative_mask);
      this.svg.appendChild(this.defs);

      this.positive_path.style.mask = `url(#${this.positive_mask_id})`;
      this.negative_path.style.mask = `url(#${this.negative_mask_id})`;

      if (this.shadow) {
        this.positive_path.style.filter = `drop-shadow(0px 3px 2px ${this.positive_color}88)`;
        this.negative_path.style.filter = `drop-shadow(0px -3px 1px ${this.negative_color}88)`;
      }
    }

    if (this.watermark.show) {
      const height = Number(this.base_line.attributes.y1.value);
      // console.log(this.draw_height);

      if (height >= 0 && height <= this.draw_height) {
        this.svg.appendChild(this.base_line);
      }
      if (this.watermark.single_color) {
        this.svg.appendChild(this.path);
      } else {
        this.svg.appendChild(this.positive_path);
        this.svg.appendChild(this.negative_path);
      }
    }

    this.container.appendChild(this.svg);
  }

  Light() {
    this.Setup();
    this.SetBoundaries();
    this.SetSvgSize();
    this.Draw();
  }
}
